import { useRef, useState } from 'react';
import {
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import NotificationsOffTwoToneIcon from '@mui/icons-material/NotificationsOffTwoTone';
import DoneAllTwoToneIcon from '@mui/icons-material/DoneAllTwoTone';
import { styled } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useNotifications, AppNotification } from 'src/Context/NotificationContext';
import {
  notificationIcon,
  severityColor,
  categoryLabel
} from 'src/Common/notifications/notificationMeta';

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
      background-color: ${theme.palette.error.main};
      color: ${theme.palette.error.contrastText};
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      font-size: 11px;
      font-weight: 700;
      box-shadow: 0 0 0 2px ${theme.palette.background.paper};
    }
`
);

function NotificationRow({
  n,
  onClick
}: {
  n: AppNotification;
  onClick: (n: AppNotification) => void;
}) {
  const theme = useTheme();
  const color = severityColor(n.severity);
  const Icon = notificationIcon(n.type, n.icon);
  const tone =
    color === 'primary'
      ? theme.palette.primary.main
      : (theme.palette as any)[color]?.main || theme.palette.primary.main;

  return (
    <ListItemButton
      onClick={() => onClick(n)}
      sx={{
        px: 2,
        py: 1.5,
        gap: 1.5,
        alignItems: 'flex-start',
        position: 'relative',
        bgcolor: n.isRead ? 'transparent' : alpha(tone, theme.palette.mode === 'dark' ? 0.12 : 0.06),
        '&:hover': { bgcolor: alpha(tone, 0.12) }
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: alpha(tone, 0.16),
          color: tone,
          width: 38,
          height: 38,
          mt: 0.25
        }}
      >
        <Icon fontSize="small" />
      </Avatar>

      <Box minWidth={0} flex={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontWeight: n.isRead ? 500 : 700, flex: 1 }}
          >
            {n.title}
          </Typography>
          {!n.isRead && (
            <Box
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone, flexShrink: 0 }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {n.message}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
          <Typography variant="caption" sx={{ color: tone, fontWeight: 600 }}>
            {categoryLabel(n.type)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ·{' '}
            {n.createdDate
              ? formatDistanceToNow(new Date(n.createdDate), { addSuffix: true })
              : ''}
          </Typography>
        </Box>
      </Box>
    </ListItemButton>
  );
}

function HeaderNotifications() {
  const { notifications, unreadCount, markAsRead, markAllRead } = useNotifications();
  const ref = useRef<any>(null);
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);

  const handleClickItem = (n: AppNotification) => {
    if (!n.isRead) markAsRead(n.id);
    setOpen(false);
    // Open the full inbox so the notification can be read in detail (inbox-style),
    // rather than deep-linking from the dropdown.
    navigate('/notifications');
  };

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton color="primary" ref={ref} onClick={() => setOpen(true)}>
          <NotificationsBadge
            badgeContent={unreadCount}
            max={99}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        open={isOpen}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 400, maxWidth: '95vw', borderRadius: 2, overflow: 'hidden' } }}
      >
        <Box
          sx={{ p: 2, pb: 1 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h5">Notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
            </Typography>
          </Box>
          <Tooltip arrow title="Mark all as read">
            <span>
              <Button
                size="small"
                startIcon={<DoneAllTwoToneIcon />}
                onClick={() => markAllRead()}
                disabled={unreadCount === 0}
              >
                Mark all
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Divider />

        <List sx={{ p: 0, maxHeight: 420, overflowY: 'auto' }} disablePadding>
          {notifications.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <NotificationsOffTwoToneIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">You're all caught up</Typography>
              <Typography variant="caption">No notifications to show here</Typography>
            </Box>
          )}
          {notifications.map((n) => (
            <Box key={n.id}>
              <NotificationRow n={n} onClick={handleClickItem} />
              <Divider component="li" />
            </Box>
          ))}
        </List>

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
